import { type ReactNode, useId } from 'react';
import type { Page } from '@rossum/api-client/pages';
import { useTheme } from '@rossum/ui/material';
import type { PageLayoutData } from '../layout/types';

const PAGE_CORNER_RADIUS = 12;

type PageContentProps = {
  pageModel: Page;
  layout: PageLayoutData;
  pageContentBlob?: string;
  isLoading: boolean;
  error: Error | null;
  isActive?: boolean;
  children?: ReactNode;
};

export const PageContent = ({
  pageModel,
  layout,
  pageContentBlob,
  isLoading,
  error,
  isActive = false,
  children,
}: PageContentProps) => {
  const theme = useTheme();
  // `useId` rather than the page id: the same page can be mounted in two
  // canvases at once, and duplicate `clipPath` ids resolve unpredictably.
  const clipPathId = `page-clip-${useId()}`;

  return (
    <g
      transform={`translate(${-layout.originalWidth / 2}, ${-layout.originalHeight / 2})`}
    >
      <rect
        x={0}
        y={0}
        width={layout.originalWidth}
        height={layout.originalHeight}
        rx={PAGE_CORNER_RADIUS}
        ry={PAGE_CORNER_RADIUS}
        fill="white"
        stroke={isActive ? theme.palette.primary.main : '#e0e0e0'}
        strokeWidth={4}
        vectorEffect="non-scaling-stroke"
      />
      {pageContentBlob ? (
        <>
          <clipPath id={clipPathId}>
            <rect
              x={0}
              y={0}
              width={layout.originalWidth}
              height={layout.originalHeight}
              rx={PAGE_CORNER_RADIUS}
              ry={PAGE_CORNER_RADIUS}
            />
          </clipPath>
          <image
            href={pageContentBlob}
            x={0}
            y={0}
            width={layout.originalWidth}
            height={layout.originalHeight}
            clipPath={`url(#${clipPathId})`}
            data-page-number={pageModel.number}
          />
        </>
      ) : isLoading ? (
        <text
          x={layout.originalWidth / 2}
          y={layout.originalHeight / 2}
          fill="#666"
          fontSize={14}
          textAnchor="middle"
        >
          Loading...
        </text>
      ) : error ? (
        <text
          x={layout.originalWidth / 2}
          y={layout.originalHeight / 2}
          fill="#d32f2f"
          fontSize={14}
          textAnchor="middle"
        >
          Error loading page
        </text>
      ) : null}
      {children}
    </g>
  );
};
